(() => {
  const SELECTOR = '[data-calinfi-odun-video-carousel]';
  const controllers = new WeakMap();

  const pauseSlideVideos = (section) => {
    section.querySelectorAll('[data-odun-video-slide]').forEach((slide) => {
      const video = slide.querySelector('video');

      slide.classList.remove('is-playing');

      if (video) {
        video.pause();
        video.controls = false;
      }
    });
  };

  const initSection = (section) => {
    if (!section || section.dataset.odunVideoInitialized === 'true') return;

    const track = section.querySelector('[data-odun-video-track]');
    const slides = Array.from(section.querySelectorAll('[data-odun-video-slide]'));
    const descriptions = Array.from(section.querySelectorAll('[data-odun-video-description]'));
    const dots = Array.from(section.querySelectorAll('[data-odun-video-dot]'));
    const prev = section.querySelector('[data-odun-video-prev]');
    const next = section.querySelector('[data-odun-video-next]');

    if (!track || slides.length === 0) return;

    section.dataset.odunVideoInitialized = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    let activeIndex = 0;
    let currentPosition = slides.length > 1 ? 1 : 0;
    controllers.set(section, controller);

    const stripEditorAttributes = (node) => {
      [node, ...node.querySelectorAll('*')].forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) {
            element.removeAttribute(attribute.name);
          }
        });

        element.removeAttribute('id');
      });
    };

    const createClone = (source, cloneIndex, positionName) => {
      const clone = source.cloneNode(true);

      stripEditorAttributes(clone);
      clone.classList.remove('is-active', 'is-playing');
      clone.classList.add('calinfi-odun-video-carousel__slide--clone', `calinfi-odun-video-carousel__slide--clone-${positionName}`);
      clone.removeAttribute('data-odun-video-slide');
      clone.dataset.odunVideoClone = String(cloneIndex);
      clone.setAttribute('aria-hidden', 'true');

      clone.querySelectorAll('video').forEach((video) => {
        video.controls = false;
        video.pause();
      });

      return clone;
    };

    let renderedSlides = slides;

    if (slides.length > 1) {
      const beforeClone = createClone(slides[slides.length - 1], slides.length - 1, 'before');
      const afterClone = createClone(slides[0], 0, 'after');

      track.insertBefore(beforeClone, slides[0]);
      track.appendChild(afterClone);
      renderedSlides = [beforeClone, ...slides, afterClone];
    }

    const getStep = () => {
      const firstSlide = renderedSlides[0];
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);

      return firstSlide.getBoundingClientRect().width + gap;
    };

    const getStablePosition = () => (slides.length > 1 ? activeIndex + 1 : 0);

    const updateState = () => {
      section.dataset.odunVideoActiveIndex = String(activeIndex);

      renderedSlides.forEach((slide, renderedIndex) => {
        const cloneIndex = slide.dataset.odunVideoClone;
        const isActive = renderedIndex === currentPosition;
        const isPreview = renderedIndex === currentPosition + 1;

        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-preview', isPreview);
        slide.setAttribute('aria-hidden', isActive || isPreview ? 'false' : 'true');

        if (cloneIndex === undefined) {
          const originalIndex = slides.indexOf(slide);
          slide.setAttribute('aria-hidden', originalIndex === activeIndex ? 'false' : 'true');
        }
      });

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      descriptions.forEach((description, descriptionIndex) => {
        const isActive = descriptionIndex === activeIndex;

        description.classList.toggle('is-active', isActive);
        description.hidden = !isActive;
        description.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    };

    const moveToPosition = (position, shouldAnimate = true) => {
      currentPosition = position;

      if (!shouldAnimate) {
        track.style.transition = 'none';
      }

      track.style.transform = `translate3d(${-getStep() * currentPosition}px, 0, 0)`;
      updateState();

      if (!shouldAnimate) {
        window.requestAnimationFrame(() => {
          track.style.transition = '';
        });
      }
    };

    const setActive = (index) => {
      const previousIndex = activeIndex;
      const nextIndex = (index + slides.length) % slides.length;
      let nextPosition = slides.length > 1 ? nextIndex + 1 : 0;

      if (slides.length > 1 && previousIndex === slides.length - 1 && nextIndex === 0 && index > previousIndex) {
        nextPosition = slides.length + 1;
      }

      if (slides.length > 1 && previousIndex === 0 && nextIndex === slides.length - 1 && index < previousIndex) {
        nextPosition = 0;
      }

      activeIndex = nextIndex;
      pauseSlideVideos(section);
      moveToPosition(nextPosition);
    };

    prev?.addEventListener('click', () => {
      setActive(activeIndex - 1);
    }, { signal });

    next?.addEventListener('click', () => {
      setActive(activeIndex + 1);
    }, { signal });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.odunVideoIndex);

        if (Number.isFinite(index)) setActive(index);
      }, { signal });
    });

    section.addEventListener('shopify:block:select', (event) => {
      const selectedSlide = event.target?.closest?.('[data-odun-video-slide]');
      const selectedIndex = slides.indexOf(selectedSlide);

      if (selectedIndex >= 0) setActive(selectedIndex);
    }, { signal });

    section.querySelectorAll('[data-odun-video-play]').forEach((button) => {
      button.addEventListener('click', () => {
        const slide = button.closest('[data-odun-video-slide]');
        const video = slide?.querySelector('video');

        if (!slide || !video) return;

        pauseSlideVideos(section);
        slide.classList.add('is-playing');
        video.controls = true;
        video.play().catch(() => {
          slide.classList.remove('is-playing');
          video.controls = false;
        });
      }, { signal });
    });

    track.addEventListener('transitionend', (event) => {
      if (event.target !== track || event.propertyName !== 'transform') return;

      if (currentPosition === 0 || currentPosition === slides.length + 1) {
        moveToPosition(getStablePosition(), false);
      }
    }, { signal });

    window.addEventListener('resize', () => {
      moveToPosition(getStablePosition(), false);
    }, { signal });

    moveToPosition(getStablePosition(), false);
  };

  document.querySelectorAll(SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);

    initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    const controller = controllers.get(section);

    if (section) pauseSlideVideos(section);
    controller?.abort();
    controllers.delete(section);

    if (section) delete section.dataset.odunVideoInitialized;
  });
})();
